import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader2 } from 'lucide-react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import ErrorFallback from './components/ErrorFallback';
import CookieConsent from './components/CookieConsent';
import { PhoneTrackingProvider } from './components/PhoneTrackingProvider';
import ProductionMonitor from './components/ProductionMonitor';
import Home from './pages/Home';
import { useAuth } from './lib/auth';

// Lazy load non-critical pages
const Services = lazy(() => import('./pages/Services'));
const Builders = lazy(() => import('./pages/Builders'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Blog = lazy(() => import('./pages/Blog'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const ProjectView = lazy(() => import('./pages/ProjectView'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

function LoadingSpinner() {
  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Simple page view tracking
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-L5GSQY4ZRG', {
        page_path: location.pathname,
      });
    }
  }, [location.pathname]);

  if (location.pathname === '/admin' && isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout>
      <ScrollToTop />
      {!isAdminRoute && <Navbar />}
      <main className="flex-grow">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/builders" element={<Builders />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/gallery/:id" element={<ProjectView />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route 
              path="/admin/dashboard/*" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && <Footer />}
      <CookieConsent />
      </Layout>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <HelmetProvider>
      <PhoneTrackingProvider>
        <Router>
          <AppContent />
        </Router>
      </PhoneTrackingProvider>
    </HelmetProvider>
  );
}

export default App;